import { useState, useEffect, useCallback, useMemo } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

type AppRole = 'admin' | 'employee';

interface AuthState {
  user: User | null;
  session: Session | null;
  role: AppRole | null;
  isLoading: boolean;
}

// Cache for user roles to avoid repeated API calls
const roleCache = new Map<string, { role: AppRole | null; timestamp: number }>();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export const useAuth = () => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    session: null,
    role: null,
    isLoading: true,
  });
  const { toast } = useToast();

  const fetchUserRole = useCallback(async (userId: string) => {
    // Check cache first
    const cached = roleCache.get(userId);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      return cached.role;
    }

    const { data, error } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', userId)
      .maybeSingle(); // Use maybeSingle instead of single to avoid error when no rows
    
    if (error) {
      console.error('Error fetching user role:', error);
      return null;
    }
    
    const role = data?.role as AppRole | null;
    
    // Cache the result
    roleCache.set(userId, { role, timestamp: Date.now() });
    
    return role;
  }, []);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setAuthState(prev => ({
          ...prev,
          session,
          user: session?.user ?? null,
        }));

        // Defer role fetch to avoid deadlock
        if (session?.user) {
          setTimeout(async () => {
            const role = await fetchUserRole(session.user.id);
            setAuthState(prev => ({
              ...prev,
              role,
              isLoading: false,
            }));
          }, 0);
        } else {
          setAuthState(prev => ({
            ...prev,
            role: null,
            isLoading: false,
          }));
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setAuthState(prev => ({
        ...prev,
        session,
        user: session?.user ?? null,
      }));

      if (session?.user) {
        fetchUserRole(session.user.id).then(role => {
          setAuthState(prev => ({
            ...prev,
            role,
            isLoading: false,
          }));
        });
      } else {
        setAuthState(prev => ({
          ...prev,
          isLoading: false,
        }));
      }
    });

    return () => subscription.unsubscribe();
  }, [fetchUserRole]);

  const signIn = async (phoneNumber: string, password: string) => {
    // Use phone number as email format for authentication
    const email = `${phoneNumber.replace(/\D/g, '')}@store.local`;
    
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      console.error('Sign in error:', error);
      if (error.message.includes('Invalid login credentials')) {
        toast({
          title: 'خطأ في تسجيل الدخول',
          description: 'رقم الموبايل أو كلمة المرور غير صحيحة',
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'خطأ',
          description: error.message,
          variant: 'destructive',
        });
      }
      return { error };
    }

    toast({
      title: 'تم تسجيل الدخول',
      description: 'مرحباً بك',
    });

    return { error: null };
  };

  const signUp = async (phoneNumber: string, password: string, displayName: string, role: AppRole) => {
    const email = `${phoneNumber.replace(/\D/g, '')}@store.local`;
    const redirectUrl = `${window.location.origin}/`;

    // First, sign up the user
    const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
      },
    });

    if (signUpError) {
      console.error('Sign up error:', signUpError);
      if (signUpError.message.includes('already registered')) {
        toast({
          title: 'خطأ',
          description: 'رقم الموبايل مسجل بالفعل',
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'خطأ',
          description: signUpError.message,
          variant: 'destructive',
        });
      }
      return { error: signUpError };
    }

    if (!signUpData.user) {
      return { error: new Error('Failed to create user') };
    }

    // Create profile with phone number
    const { error: profileError } = await supabase
      .from('profiles')
      .insert({
        user_id: signUpData.user.id,
        phone_number: phoneNumber,
        display_name: displayName,
      });

    if (profileError) {
      console.error('Profile creation error:', profileError);
      if (profileError.message.includes('duplicate')) {
        toast({
          title: 'خطأ',
          description: 'رقم الموبايل مسجل بالفعل',
          variant: 'destructive',
        });
      }
      return { error: profileError };
    }

    // Assign role using admin function (bypasses RLS)
    const { error: roleError } = await supabase.rpc('admin_insert_user_role', {
      target_user_id: signUpData.user.id,
      target_role: role,
    });

    if (roleError) {
      console.error('Role assignment error:', roleError);
      toast({
        title: 'خطأ في تعيين الصلاحية',
        description: 'تم إنشاء الحساب لكن لم يتم تعيين الدور',
        variant: 'destructive',
      });
      return { error: roleError };
    }

    toast({
      title: 'تم إنشاء الحساب',
      description: 'يمكنك الآن تسجيل الدخول',
    });

    return { error: null, user: signUpData.user };
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      toast({
        title: 'خطأ',
        description: error.message,
        variant: 'destructive',
      });
      return { error };
    }

    toast({
      title: 'تم تسجيل الخروج',
    });

    return { error: null };
  };

  return {
    ...authState,
    signIn,
    signUp,
    signOut,
    isAdmin: authState.role === 'admin',
    isEmployee: authState.role === 'employee',
    isStaff: authState.role === 'admin' || authState.role === 'employee',
  };
};
